import { useOffreService } from '@europrocurement/l2d-domain';
import { OriginesOffresJsonldOrigineOffreRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import React, { createContext, PropsWithChildren, useContext, useEffect, useReducer } from 'react';
import { DataProviderProps, DataState } from './types';

export type OrigineOffre = {
    id: number;
    default: boolean;
    dateDebut: string;
    dateFin: string | null;
    offreId: number;
    origineId: number;
};

type OriginesOffresState = DataState & {
    data: Array<OrigineOffre>;
};

const INIT_ORIGINES_OFFRES_CTX = 'init_origines_offres_ctx';

type Action = { type: 'init_origines_offres_ctx'; payload: Array<OrigineOffre> };

export const OriginesOffresContext = createContext<OriginesOffresState | null>(null);
export const OriginesOffresDispatchContext = createContext<React.Dispatch<Action> | null>(null);

export const origineOffreApiToStateTransformer = (
    item: OriginesOffresJsonldOrigineOffreRead,
): OrigineOffre => ({
    id: item.id as number,
    default: item.default ?? false,
    dateDebut: item.dateDebut as string,
    dateFin: item.dateFin ?? null,
    offreId: item.offre?.id as number,
    origineId: item.origine?.id as number,
});

function originesOffresReducer(_state: OriginesOffresState, action: Action): OriginesOffresState {
    switch (action.type) {
        case INIT_ORIGINES_OFFRES_CTX:
            return { loaded: true, data: [...action.payload] };
        default:
            throw new Error(`Unknown action: ${(action as Action).type}`);
    }
}

export const OriginesOffresProvider: React.FunctionComponent<PropsWithChildren<DataProviderProps>> =
    function ({ autoload, children }) {
        const [state, dispatch] = useReducer(originesOffresReducer, { loaded: false, data: [] });
        const { offerOriginsApi } = useOffreService();

        useEffect(() => {
            if (!autoload) {
                return;
            }

            offerOriginsApi
                .apiOriginesOffresGetCollection({ itemsPerPage: 999 })
                .then((response) => {
                    dispatch({
                        type: INIT_ORIGINES_OFFRES_CTX,
                        payload: response.data['hydra:member'].map(
                            origineOffreApiToStateTransformer,
                        ),
                    });
                })
                .catch((e) => {
                    console.error(e);
                    dispatch({ type: INIT_ORIGINES_OFFRES_CTX, payload: [] });
                });
        }, [offerOriginsApi, autoload]);

        return (
            <OriginesOffresContext.Provider value={state}>
                <OriginesOffresDispatchContext.Provider value={dispatch}>
                    {children}
                </OriginesOffresDispatchContext.Provider>
            </OriginesOffresContext.Provider>
        );
    };

export function useOriginesOffresCtx() {
    const state = useContext(OriginesOffresContext);

    const isDataLoaded = (): boolean => state?.loaded || false;

    const getOriginesOffres = (): Array<OrigineOffre> => state?.data || [];

    const getOrigineOffreById = (id: number): OrigineOffre | undefined =>
        (state?.data || []).find((origineOffre) => origineOffre.id === id);

    const getOriginesOffresByOffreId = (offreId: number): Array<OrigineOffre> =>
        (state?.data || []).filter((origineOffre) => origineOffre.offreId === offreId);

    const getOriginesOffresByOrigineId = (origineId: number): Array<OrigineOffre> =>
        (state?.data || []).filter((origineOffre) => origineOffre.origineId === origineId);

    return {
        isDataLoaded,
        getOriginesOffres,
        getOrigineOffreById,
        getOriginesOffresByOffreId,
        getOriginesOffresByOrigineId,
    };
}

export function useOriginesOffresCtxDispatch() {
    return useContext(OriginesOffresDispatchContext);
}
